import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { contactAPI } from '../services/api';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import { validateForm } from '../utils/validation';

const Contact = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Auto-fill subject from navigation state (Stage 1/Stage 2 buttons)
  useEffect(() => {
    if (location.state?.productName && location.state?.stage) {
      const { productName, stage } = location.state;
      const subjectPrefix = stage === 1 ? t('contact.stage1Subject') : t('contact.stage2Subject');
      const subject = `${subjectPrefix} ${productName}`;
      setFormData(prev => ({ ...prev, subject }));
    }
  }, [location.state, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationRules = {
      name: { required: true, minLength: 2, label: 'Name' },
      email: { required: true, email: true },
      phone: { required: true, phone: true },
      subject: { required: true, minLength: 3, label: 'Subject' },
      message: { required: true, minLength: 10, label: 'Message' }
    };
    
    const validationErrors = validateForm(formData, validationRules, t);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(t('contact.validationError'), { autoClose: 2000 });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await contactAPI.send(formData);
      
      setSubmitStatus('success');
      toast.success(t('toast.messageSent'), { autoClose: 1000 });
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail;
        if (Array.isArray(validationErrors)) {
          const errorMsg = validationErrors.map(err => err.msg).join(', ');
          toast.error(`Validation error: ${errorMsg}`, { autoClose: 1000 });
        } else {
          toast.error(t('toast.emailValidationError'), { autoClose: 1000 });
        }
      } else {
        toast.error(error.response?.data?.detail || t('toast.messageFailed'), { autoClose: 1000 });
      }
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={t('contact.pageTitle')}
        description={t('contact.pageDescription')}
        keywords={t('seo.contact.keywords')}
        type="website"
      />

      {/* Hero Section */}
      <section className="relative bg-racing-black text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-racing-black opacity-50"></div>
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-display font-bold text-racing-black mb-6">
                {t('contact.getInTouch')}
              </h2>
              <p className="text-gray-600 mb-8">
                {t('contact.description')}
              </p>

              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.email')}</h3>
                    <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'meller@racing.hub.pl'}`} className="text-racing-red hover:underline">
                      {process.env.REACT_APP_CONTACT_EMAIL || 'meller@racing.hub.pl'}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.phone')}</h3>
                    <a href={`tel:${(process.env.REACT_APP_CONTACT_PHONE || '+48 731 662 005').replace(/\s/g, '')}`} className="text-racing-red hover:underline">
                      {process.env.REACT_APP_CONTACT_PHONE || '+48 731 662 005'}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.address')}</h3>
                    <p className="text-gray-600">
                      {process.env.REACT_APP_COMPANY_NAME || 'Meller Racing'}<br />
                      {process.env.REACT_APP_ADDRESS_STREET || 'Świętego Jana 13'}<br />
                      {process.env.REACT_APP_ADDRESS_POSTAL || '43-267'} {process.env.REACT_APP_ADDRESS_CITY || 'Suszec'}, {process.env.REACT_APP_ADDRESS_COUNTRY || 'Poland'}<br />
                      {process.env.REACT_APP_NIP || 'NIP: 6381866164'}
                    </p>
                  </div>
                </div>

                {/* Social Media */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.socialMedia')}</h3>
                    <div className="flex space-x-4">
                      <a
                        href={process.env.REACT_APP_FACEBOOK_URL || 'https://www.facebook.com/p/Meller-Racing-warsztat-i-hamownia-motocyklowa-100083175588341/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-racing-red hover:text-racing-darkRed transition"
                      >
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </a>
                      <a
                        href={process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/meller_racing_/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-racing-red hover:text-racing-darkRed transition"
                      >
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Google Maps */}
                <div className="mt-8 pt-6 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('contact.visitUsInPerson')}</h3>
                  <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2560.4944798658795!2d18.7819575!3d50.0337799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4716b39fd6f7b977%3A0xfc21082ddd12aaf8!2sMeller%20Racing%2Fhamownia%20motocyklowa%20i%20serwis!5e0!3m2!1sen!2spl!4v1642000000000!5m2!1sen!2spl"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Meller Racing Location"
                    ></iframe>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-display font-bold text-racing-black mb-8">
                  {t('contact.sendMessage')}
                </h2>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    {t('contact.successMessage')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    {t('contact.errorMessage')}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red focus:bg-white transition ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder={t('contact.namePlaceholder')}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red focus:bg-white transition ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder={t('contact.emailPlaceholder')}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.phone')} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red focus:bg-white transition ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder={t('contact.phonePlaceholder')}
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.subject')} *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red focus:bg-white transition ${errors.subject ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder={t('contact.subjectPlaceholder')}
                    />
                    {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red focus:bg-white transition resize-none ${errors.message ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder={t('contact.messagePlaceholder')}
                    ></textarea>
                    {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-racing-red hover:bg-racing-darkRed text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-lg"
                  >
                    {isSubmitting ? t('contact.sending') : t('contact.send')}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
